import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { store } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { toast } from 'sonner';

// Auth error codes that should trigger logout
const AUTH_ERROR_CODES = [
  'INVALID_TOKEN',
  'UNAUTHENTICATED',
  'UNAUTHORIZED',
  'TOKEN_EXPIRED',
  'FORBIDDEN',
];

// Auth error message patterns
const AUTH_ERROR_PATTERNS = [
  'invalid token',
  'token expired',
  'expired token',
  'invalid or expired token',
  'not authenticated',
  'unauthorized',
  'unauthenticated',
  'authentication failed',
  'jwt expired',
  'jwt malformed',
];

// Function to check if message contains auth error pattern
function containsAuthErrorPattern(text: string): boolean {
  if (!text) return false;
  const lowerText = text.toLowerCase();
  return AUTH_ERROR_PATTERNS.some(pattern => lowerText.includes(pattern));
}

// Function to handle logout with cleanup
const handleAuthError = (message?: string) => {
  // Prevent multiple logout attempts
  const authState = store.getState().auth;
  if (!authState.isAuthenticated) return;

  // Show toast notification
  toast.error(message || 'Session expired. Please login again.', {
    duration: 3000,
  });

  // Dispatch logout action
  store.dispatch(logout());

  // Clear all storage
  localStorage.clear();
  sessionStorage.clear();

  // Redirect to login after a short delay to show toast
  setTimeout(() => {
    window.location.href = '/login';
  }, 500);
};

// Custom fetch that intercepts responses and checks for auth errors
const authCheckingFetch = async (uri: RequestInfo | URL, options?: RequestInit): Promise<Response> => {
  const response = await fetch(uri, options);

  // Clone response to read body without consuming it
  const clonedResponse = response.clone();

  try {
    const body = await clonedResponse.json();

    // Check if response contains auth errors
    if (body.errors && Array.isArray(body.errors)) {
      for (const err of body.errors) {
        const extensions = err.extensions || {};
        const errorCode = (extensions.code || '').toUpperCase();
        const statusCode = extensions.statusCode || extensions.http?.status;
        const message = err.message || '';

        const isAuthErr =
          AUTH_ERROR_CODES.includes(errorCode) ||
          statusCode === 401 ||
          statusCode === 403 ||
          containsAuthErrorPattern(message);

        if (isAuthErr) {
          console.warn('[Apollo Fetch Interceptor] Auth error detected:', {
            code: errorCode,
            statusCode,
            message
          });
          handleAuthError(message || 'Session expired. Please login again.');
          break;
        }
      }
    }
  } catch {
    // Ignore JSON parse errors - response might not be JSON
  }

  return response;
};

// HTTP Link - Your GraphQL endpoint with custom fetch
const httpLink = createHttpLink({
  uri: (import.meta as any).env.VITE_GRAPHQL_ENDPOINT || 'https://platform.instadev.ai/graphql',
  fetch: authCheckingFetch,
});

// Auth Link - Adds authorization header to every request
const authLink = setContext((_, { headers }) => {
  // Get the current auth state from Redux store
  const authState = store.getState().auth;
  const token = authState.accessToken;

  // Return the headers to the context
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Error Link - Handle GraphQL errors and authentication failures
const errorLink = onError((errorResponse: any) => {
  const { graphQLErrors, networkError, response, operation } = errorResponse;

  console.log('[Apollo errorLink] Full error response:', errorResponse);
  console.log('[Apollo errorLink] Error response received:', { graphQLErrors, networkError, response });

  // Helper function to check and handle auth errors
  const checkAndHandleAuthError = (error: any) => {
    const { message, extensions } = error;
    const errorCode = extensions?.code?.toUpperCase();
    const statusCode = extensions?.statusCode || extensions?.http?.status;

    console.log('[Apollo errorLink] Checking error:', { message, errorCode, statusCode });

    const isAuthError =
      AUTH_ERROR_CODES.includes(errorCode) ||
      statusCode === 401 ||
      statusCode === 403 ||
      message?.toLowerCase().includes('invalid token') ||
      message?.toLowerCase().includes('token expired') ||
      message?.toLowerCase().includes('not authenticated') ||
      message?.toLowerCase().includes('unauthorized');

    if (isAuthError) {
      console.warn('[Auth Error]: Token invalid or expired, logging out...');
      handleAuthError(message || 'Invalid or expired token. Please login again.');
      return true;
    }
    return false;
  };

  // Check graphQLErrors if available
  if (graphQLErrors && graphQLErrors.length > 0) {
    for (const error of graphQLErrors) {
      console.log('[Apollo errorLink] GraphQL error details:', error);
      if (checkAndHandleAuthError(error)) {
        return;
      }
    }
  }

  // Check response.errors if graphQLErrors is not populated (Apollo Client 4.x issue)
  if (response?.errors && response.errors.length > 0) {
    console.log('[Apollo errorLink] Response errors found:', response.errors);
    for (const error of response.errors) {
      if (checkAndHandleAuthError(error)) {
        return;
      }
    }
  }

  // Check networkError for auth issues
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);

    // Check if networkError has result with errors (common in Apollo Client 4.x)
    const result = (networkError as any).result;
    if (result?.errors && result.errors.length > 0) {
      console.log('[Apollo errorLink] Network error with result.errors:', result.errors);
      for (const error of result.errors) {
        if (checkAndHandleAuthError(error)) {
          return;
        }
      }
    }

    // Handle network-level 401/403 errors
    const statusCode = (networkError as any).statusCode || (networkError as any).status;
    if (statusCode === 401 || statusCode === 403) {
      console.warn('[Network Auth Error]: Unauthorized, logging out...');
      handleAuthError('Session expired. Please login again.');
    }
  }
});

// Create Apollo Client
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]), // Chain the links
  cache: new InMemoryCache({
    // Optional: Configure cache policies
    typePolicies: {
      Query: {
        fields: {
          // Add field policies if needed
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});
